import { InteractionScreen, setScreen, setSelectedOption, setScreenPage } from "../Redux/screenInteractionsSlice";
import { setModal, ModalType, setSound } from "../Redux/uiSlice";
import { managePointer } from "../components/Resolvers/ButtonActionResolver/helpers/managePointer";
import { postGameMessage } from "../components/Screen/ScreenInteractions";
import { KeyEvent } from "./keyboardEventListener";
import { useAppDispatch, useAppSelector } from "../Redux/store";
import { Direction } from "../components/DirectionalButtons/DirectionButton";
import { useEffect } from "react";
import { ActionButtonType } from "../components/ActionButtons/ActionButton";
import { OptionButtonType } from "../components/ActionButtons/HomeButton";
import { mapEvent } from "../helpers/messages";
import { setDemoGame } from "../Redux/gameSlice";
import { removeLastCharacter } from "../helpers/string";

export type Button = Direction | OptionButtonType | ActionButtonType;

export interface IInternalMessage {
    wowboi: true, event: KeyEvent, button: Button
}

export const useActionButtons = () => {
    const dispatch = useAppDispatch();

    const onMessage = (e: MessageEvent<IInternalMessage>) => {
        actionValues?.[screen]?.[e.data.button]?.(e.data)
    }

    useEffect(() => {
        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('message', onMessage);
        }
    });

    const { optionSelected = 0, screenPage, sound, screen } = useAppSelector(state => ({
        optionSelected: state.screenInteractions.optionSelected,
        screenPage: state.screenInteractions.screenPage,
        sound: state.ui.sound,
        screen: state.screenInteractions.screen,
        gameUrl: state.game.gameUrl
    }));

    const actionValues = {
        [InteractionScreen.WELCOME]: {
            x: () => {
                dispatch(setScreen(InteractionScreen.HOME))
            },
            z: undefined,
            up: undefined,
            right: undefined,
            down: undefined,
            left: undefined,
            start: undefined,
            home: undefined
        },
        [InteractionScreen.HOME]: {
            x: () => {
                const options = {
                    '0': () => {
                        dispatch(setScreen(InteractionScreen.GAME))
                    },
                    '1': () => dispatch(setModal(ModalType.INSERTGAME)),
                    '2': () => dispatch(setScreen(InteractionScreen.ABOUT)),
                    '3': () => window.open('https://wowboi.notion.site/WowBoi-Developer-Guide-a5de706805bf484dbd27a1620d1451d1?pvs=4', '_blank')?.focus(),
                    '4': () => window.open('https://wowboi.notion.site/WowBoi-Roadmap-47ed12f2c8ac459d9ae096f2528508dd?pvs=4', '_blank')?.focus(),
                    '5': () => window.open('https://bellscribe.com/collections/wowboi-cartridge-one')?.focus(),
                    '6': () => {
                        dispatch(setScreen(InteractionScreen.LINKS));
                        dispatch(setSelectedOption(0))
                    },
                }
                const key = (optionSelected.toString() as keyof typeof options);

                options[key]();
            },
            z: () => dispatch(setScreen(InteractionScreen.WELCOME)),
            up: (e: IInternalMessage) => {
                dispatch(setSelectedOption(managePointer(optionSelected, (e.button as Direction), 7)))
            },
            right: (e: IInternalMessage) => {
                dispatch(setSelectedOption(managePointer(optionSelected, (e.button as Direction), 7)))
            },
            down: (e: IInternalMessage) => {
                dispatch(setSelectedOption(managePointer(optionSelected, (e.button as Direction), 7)))
            },
            left: (e: IInternalMessage) => {
                dispatch(setSelectedOption(managePointer(optionSelected, (e.button as Direction), 7)))
            },
            start: undefined,
            home: undefined
        },
        [InteractionScreen.GAME]: {
            up: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            right: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            down: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            left: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            x: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            z: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            start: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            home: () => {
                dispatch(setScreen(InteractionScreen.HOME));
                dispatch(setSelectedOption(0));
            }
        },
        [InteractionScreen.ABOUT]: {
            x: () => {
                if (screenPage === 6) {
                    dispatch(setScreen(InteractionScreen.HOME));
                    dispatch(setScreenPage(0));
                } else {
                    dispatch(setScreenPage(screenPage + 1));
                }
            },
            z: () => {
                screenPage === 0 ? dispatch(setScreen(InteractionScreen.HOME)) : dispatch(setScreenPage(screenPage - 1))
            },
            up: () => { },
            right: () => { },
            down: () => { },
            left: () => { },
            start: undefined,
            home: () => {
                dispatch(setScreen(InteractionScreen.HOME));
                dispatch(setSelectedOption(0));
            }
        },
        [InteractionScreen.OPTIONS]: {
            x: () => {
                localStorage.setItem('wowboi-sound', sound ? 'off' : 'on');
                dispatch(setSound(!sound));
            },
            z: () => {
                dispatch(setScreen(InteractionScreen.HOME));
            },
            up: undefined,
            right: undefined,
            down: undefined,
            left: undefined,
            start: undefined,
            home: () => {
                dispatch(setScreen(InteractionScreen.HOME));
                dispatch(setSelectedOption(0));
            }
        },
        [InteractionScreen.ROADMAP]: {
            x: undefined,
            z: () => {
                dispatch(setScreen(InteractionScreen.HOME));
            },
            up: undefined,
            right: undefined,
            down: undefined,
            left: undefined,
            start: undefined,
            home: () => {
                dispatch(setScreen(InteractionScreen.HOME));
                dispatch(setSelectedOption(0))
            }
        },
        [InteractionScreen.LINKS]: {
            x: () => {
                const links = {
                    '0': 'https://twitter.com/wowboi69',
                    '1': 'https://twitter.com/frompy_',
                    '2': `${removeLastCharacter(window.location.href)}/assets`
                }

                const key = optionSelected.toString() as keyof typeof links;

                window.open(links[key] ?? '', '_blank')?.focus();
            },
            z: () => {
                dispatch(setScreen(InteractionScreen.HOME));
                dispatch(setSelectedOption(5));
            },
            up: () => {
                if (optionSelected > 0) {
                    dispatch(setSelectedOption(optionSelected - 1));
                }
            },
            down: () => {
                if (optionSelected < 3) {
                    dispatch(setSelectedOption(optionSelected + 1));
                }
            },
            right: undefined,
            left: undefined,
            start: undefined,
            home: () => {
                dispatch(setScreen(InteractionScreen.HOME));
                dispatch(setSelectedOption(0));
            }
        },
        [InteractionScreen.SANDBOX]: {
            up: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            right: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            down: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            left: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            x: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            z: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            start: (e: IInternalMessage) => postGameMessage(mapEvent(e)),
            home: () => {
                // dispatch(setScreen(InteractionScreen.HOME));
                // dispatch(setSelectedOption(0))
            }
        },
        [InteractionScreen.DEMO_SELECT]: {
            x: () => {
                const options = {
                    '0': () => {
                        dispatch(setScreen(InteractionScreen.GAME));
                        dispatch(setDemoGame('bellsn8ke'))
                    },
                    '1': () => {
                        dispatch(setScreen(InteractionScreen.GAME));
                        dispatch(setDemoGame('astercoins'))
                    },
                }
                const key = (optionSelected.toString() as keyof typeof options);

                options[key]();
            },
            z: () => dispatch(setScreen(InteractionScreen.WELCOME)),
            up: () => dispatch(setSelectedOption(0)),
            right: undefined,
            down: () => dispatch(setSelectedOption(1)),
            left: undefined,
            start: undefined,
            home: () => {
                dispatch(setScreen(InteractionScreen.HOME));
                dispatch(setSelectedOption(0));
            }
        }
    }

    return actionValues[screen];
}